import type { MessagesShape } from './types.js';

export const zhCN: MessagesShape = {
  common: {
    ok: '确定',
    cancel: '取消',
    save: '保存',
    delete: '删除',
    close: '关闭',
    back: '返回',
    search: '搜索',
    loading: '加载中',
    empty: '暂无内容',
    retry: '重试',
    clear: '清除',
    remove: '移除',
  },
  dialog: {
    confirm: {
      title: '确认删除',
      body: '此操作不可撤销。',
      confirmBtn: '删除',
    },
  },
  toolbar: {
    theme: '主题',
    density: '密度',
    accent: '强调色',
    layout: '布局',
    language: '语言',
    radius: '圆角',
    surface: '表面',
    motion: '动效',
    shadow: '阴影',
    typescale: '字号',
    grid: '画布',
  },
  layout: {
    writing: '写作',
    reading: '阅读',
    focus: '专注',
    research: '研究',
    review: '审阅',
    immersive: '沉浸',
  },
  a11y: {
    closeButton: '关闭',
    menuButton: '菜单',
    openMenu: '打开导航',
    closeMenu: '关闭导航',
    skipToMain: '跳到主内容',
    skipToNav: '跳到导航',
    liveRegionLabel: '状态通告',
  },
  select: {
    searchPlaceholder: '搜索…',
    empty: '无选项',
    noMatch: '无匹配 "{query}"',
    announceSelected: '已选择 {label}',
    defaultLabel: '下拉选择',
  },
  selectTag: {
    add: '添加',
    placeholder: '选择标签',
    createOption: '添加“{value}”',
    removeLabel: '移除 {label}',
    selectedCount: '已选择 {count} 个',
  },
  input: {
    clearLabel: '清除',
    defaultLabel: '文本输入',
  },
  textarea: {
    clearLabel: '清除',
    defaultLabel: '多行输入',
  },
  chip: {
    removeLabel: '移除',
    defaultLabel: '标签',
  },
  badge: {
    defaultLabel: '徽章',
  },
  tag: {
    defaultLabel: '标签',
    removeLabel: '移除',
  },
  kbd: {
    defaultLabel: '快捷键',
  },
  slider: {
    defaultLabel: '滑杆',
  },
  progress: {
    defaultLabel: '进度',
  },
  spinner: {
    defaultLabel: '加载中',
  },
  switch: {
    defaultLabel: '开关',
  },
  checkbox: {
    defaultLabel: '复选框',
  },
  radio: {
    defaultLabel: '单选项',
  },
  segmented: {
    defaultLabel: '分段控件',
  },
  menu: {
    defaultLabel: '菜单',
  },
  commandPalette: {
    defaultLabel: '命令面板',
    searchPlaceholder: '输入命令或搜索…',
    empty: '无匹配命令',
    navigateHint: '导航',
    selectHint: '选择',
    closeHint: '关闭',
  },
  drawer: {
    defaultLabel: '抽屉面板',
  },
  iconButton: {
    loadingLabel: '处理中',
    defaultLabel: '图标按钮',
  },
  playground: {
    brand: 'ViquardDoc',
    tagline: 'doc-ui 演示场',
    menuOpen: '展开导航',
    menuClose: '收起导航',
    storiesNav: '组件导航',
    breadcrumbLabel: '当前位置',
    inspector: {
      eyebrow: '检视面板',
      emptyTitle: '属性矩阵',
      emptyBody:
        '当前 Story 未提供检视内容。在此可集中呈现尺寸、变体、形状等属性网格,让中央画布专注于交互样例。',
      sectionMeta: '上下文',
      metaStory: 'story',
      metaGroup: 'group',
      metaTheme: 'theme',
      metaAccent: 'accent',
      metaLocale: 'locale',
    },
  },
  stories: {
    groups: {
      foundation: '基石',
      inputs: '输入控件',
      display: '信息展示',
      feedback: '反馈',
      boundary: '边界态',
      icons: '图标',
      overlays: '浮层',
      menus: '菜单',
      navigation: '导航',
      a11y: '无障碍',
      shortcuts: '键盘',
      data: '数据',
      layout: '布局',
    },
    titles: {
      theme: '主题',
      iconGallery: '图标库',
      button: '按钮',
      iconButton: '图标按钮',
      forms: '表单',
      select: '下拉选择',
      segmented: '分段',
      slider: '滑杆',
      display: '信息展示',
      feedback: '反馈',
      boundary: '边界态',
      overlays: '浮层与对话框',
      menus: '菜单族',
      commandPalette: '命令面板',
      drawer: '抽屉',
      tabs: '标签页',
      accordion: '手风琴',
      breadcrumb: '面包屑',
      avatar: '头像',
      tree: '树',
      table: '表格',
      list: '列表',
      timeline: '时间线',
      splitPane: '分栏',
      numberInput: '数值输入',
      pagination: '分页',
      a11yUtilities: '无障碍工具',
      shortcuts: '快捷键',
      dataView: '数据视图',
      virtualization: '虚拟化',
      newInputs: '复杂输入',
      presence: '在线状态',
      settingsHub: '设置中心',
      formsKit: '表单套件',
      dndDemo: '拖拽',
    },
  },
  axis: {
    density: { compact: '紧凑', comfortable: '舒适', spacious: '宽松' },
    radius: { crisp: '直角', soft: '柔角', pillowy: '软垫', velvet: '丝绒' },
    surface: { floating: '浮层', flush: '齐平', hybrid: '混合' },
    motion: {
      reduced: '减少动效',
      performance: '性能',
      standard: '标准',
      advanced: '高级',
      rich: '丰富',
    },
    shadow: { flat: '扁平', soft: '柔和', pronounced: '明显', dramatic: '戏剧' },
    typescale: { compact: '紧凑', standard: '标准', large: '宽大' },
    grid: { dots: '点阵', grid: '网格', lines: '横线', blank: '空白' },
    theme: {
      system: '跟随系统',
      light: '明亮',
      dark: '暗黑',
      sepia: '羊皮',
      eyecare: '护眼',
      chalkboard: '黑板',
      parchment: '古卷',
    },
    accent: {
      ochre: '赭黄',
      ink: '墨黑',
      moss: '苔绿',
      indigo: '靛蓝',
      crimson: '绛红',
      custom: '自定义',
    },
    locale: { 'zh-CN': '中文', 'en-US': 'English' },
  },
};
